// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { compose } from 'redux';

import { withPageMeta } from '@gandi/app-runner';
import { needsAuth } from '@gandi/authentication';
import { selectOrder } from '@gandi/billing';
import { sendCommerce, sendBulkCommerce } from '@gandi/tracking';

import { useQuery } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { Layout } from '../components/Layout';

import { formatOrderItem } from './helpers/formatOrderItem';
import { getTemporaryCartTracking } from './modules';

type Query = {
  order: string,
};

/** According to https://developers.atinternet-solutions.com/piano-analytics/data-collection/how-to-send-events/sales-insights#cart-properties
 * you need to generate a new unique version identifier everytime the cart is updated while awaiting payment, for example if the customer bought a product,
 * but we're actually out of stock */
const CART_VERSION = 'v1';

function PaymentReturn() {
  const dispatch = useDispatch();
  const { order: orderId } = useQuery<Query>();
  const order = useAppSelector(
    state => orderId && selectOrder(state, { orderId }),
  );

  const temporaryCart = getTemporaryCartTracking();

  React.useEffect(() => {
    if (temporaryCart && order) {
      const { cartId, discounts, distinctProducts, cartProducts, currency } =
        temporaryCart;

      sendCommerce({
        name: 'cart.awaiting_payment',
        data: {
          cart_id: cartId,
          cart_version: CART_VERSION,
          cart_currency: order.currency,
          cart_turnovertaxincluded: order.price_after_taxes,
          cart_turnovertaxfree: order.price_before_taxes,
          cart_quantity: order.items.length,
          cart_nbdistinctproduct: distinctProducts,
          shipping_delivery: 'none',
          shipping_costtaxincluded: 0,
          shipping_costtaxfree: 0,
          payment_mode: order.payment_type,
          transaction_id: order.id,
          transaction_promocode: discounts,
          transaction_firstpurchase: true,
        },
      });

      const bulkProducts = cartProducts.map(p => ({
        name: 'product.awaiting_payment',
        data: {
          cart_id: cartId,
          cart_version: CART_VERSION,
          ...formatOrderItem({ ...p, currency }),
        },
      }));

      // $FlowIgnore[incompatible-call] - we need to stay flexible with the tracking lib
      sendBulkCommerce(bulkProducts);
    }
  }, [order, dispatch, temporaryCart]);
  return (
    <Layout currentStep={5} backgroundColor="surface">
      <Outlet />
    </Layout>
  );
}

export default (compose(
  needsAuth,
  withPageMeta(args => ({
    title: args.__('Payment confirmation - Gandi.net'),
  })),
)(PaymentReturn): React.ComponentType<{}>);
